import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from '@mui/material';

const EnvironmentExportFilters = ({
  sourceProvider,
  discipline,
  selectedSeasons,
  startDate,
  endDate,
  providerOptions,
  disciplineOptions,
  seasonOptions,
  competitionOptions,
  selectedCompetitions,
  competitionsLoading,
  providersLoading,
  canSubmit,
  onSourceProviderChange,
  onDisciplineChange,
  onSeasonsChange,
  onStartDateChange,
  onEndDateChange,
  onCompetitionsChange,
  onSubmit,
}) => {
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Critères d'export
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Sélectionnez le provider source et la discipline, puis utilisez les saisons si besoin pour réduire la liste des compétitions. Les dates permettent ensuite de limiter le périmètre des matchs.
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              freeSolo
              options={providerOptions}
              value={sourceProvider || ''}
              loading={providersLoading}
              onChange={(_, value) => onSourceProviderChange(value || '')}
              onInputChange={(_, value, reason) => {
                if (reason === 'input') onSourceProviderChange(value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Provider source"
                  required
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {providersLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              freeSolo
              options={disciplineOptions}
              value={discipline || ''}
              disabled={!sourceProvider || competitionsLoading}
              onChange={(_, value) => onDisciplineChange(value || '')}
              onInputChange={(_, value, reason) => {
                if (reason === 'input') onDisciplineChange(value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Discipline"
                  required
                  helperText={!sourceProvider ? 'Choisissez d’abord un provider source.' : ''}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={seasonOptions}
              value={selectedSeasons}
              disabled={!sourceProvider || !discipline || competitionsLoading}
              onChange={(_, values) => onSeasonsChange(values)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Saisons (facultatif)"
                  helperText={
                    !discipline
                      ? 'Choisissez une discipline pour afficher les saisons disponibles.'
                      : selectedSeasons.length === 0
                      ? `Toutes les saisons sont affichées (${seasonOptions.length} disponible(s)).`
                      : `${selectedSeasons.length} saison(s) sélectionnée(s).`
                  }
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={competitionOptions}
              value={selectedCompetitions}
              loading={competitionsLoading}
              disabled={!sourceProvider || !discipline}
              getOptionLabel={(option) => {
                const season = option?.season ? ` — ${option.season}` : '';
                return `${option?.name || 'Compétition sans nom'}${season}`;
              }}
              isOptionEqualToValue={(option, value) => String(option?._id) === String(value?._id)}
              onChange={(_, values) => onCompetitionsChange(values)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Compétitions"
                  required
                  helperText={
                    !discipline
                      ? 'Choisissez une discipline pour afficher les compétitions disponibles.'
                      : `${competitionOptions.length} compétition(s) disponible(s)${
                          selectedSeasons.length > 0 ? ' pour les saisons sélectionnées' : ''
                        }.`
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {competitionsLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2">Périmètre temporel (facultatif)</Typography>
              <Typography variant="body2" color="text.secondary">
                Sans date, tous les matchs et tous les rattachements Player.teamsSeason vers les équipes exportées sont conservés. Avec une plage de dates, Match.dateTime est filtré et seuls les joueurs ayant une période teamsSeason qui chevauche cette plage sont exportés. Player.teams est reconstruit depuis ces périodes.
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Date début"
              type="date"
              fullWidth
              value={startDate}
              onChange={(event) => onStartDateChange(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Date fin"
              type="date"
              fullWidth
              value={endDate}
              onChange={(event) => onEndDateChange(event.target.value)}
              InputLabelProps={{ shrink: true }}
              error={Boolean(startDate && endDate && startDate > endDate)}
              helperText={
                startDate && endDate && startDate > endDate
                  ? 'La date de fin doit être postérieure à la date de début.'
                  : ''
              }
            />
          </Grid>

        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button variant="contained" disabled={!canSubmit} onClick={onSubmit}>
            Vérifier l'export
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default EnvironmentExportFilters;
